import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the campus slice
const initialState = {
  campusData: {
    data: [], // Initialize data as an empty array
  },
  status: "idle",
  error: null,
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Async thunk for adding a new campus.
 * @param {Object} data - The campus data to be added.
 * @returns {Object} - The added campus data.
 */
export const addCampus = createAsyncThunk(
  "createCampus",
  async ({ data, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: data,
          url: "admin/campus",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.createCampus.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for fetching the list of campuses.
 * @returns {Array} - List of campuses.
 */
export const campusList = createAsyncThunk(
  "listCampus",
  async ({ pageCount, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/campus?page=${pageCount}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.listCampus.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for updating an existing campus.
 * @param {Object} data - The campus data to be updated.
 * @param {string} id - The ID of the campus to be updated.
 * @returns {Object} - The updated campus data.
 */
export const updateCampus = createAsyncThunk(
  "editCampus",
  async ({ data, id, navigate }, { rejectWithValue }) => {
    try {
      await httpRequest(
        {
          method: "PUT",
          body: data,
          url: `admin/campus/${id}`,
        },
        navigate
      );
      return { data, id };
    } catch (err) {
      return rejectWithValue(err.message || messages.editCampus.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for fetching a single campus by ID.
 * Note: Currently not in use.
 * @param {string} id - The ID of the campus to be fetched.
 * @returns {Object} - The fetched campus data.
 */
export const singleCampus = createAsyncThunk(
  "singleCampus",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/campus/${id}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.singleCampus.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for deleting a campus by ID.
 * @param {string} id - The ID of the campus to be deleted.
 * @returns {Object} - The response from the delete request.
 */
export const deleteCampus = createAsyncThunk(
  "deleteCampus",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "DELETE",
          url: `admin/campus/${id}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.deleteCampus.error, {
        cause: err.cause,
      });
    }
  }
);

// Create a slice for campus-related actions and state management
const campusSlice = createSlice({
  name: "campus",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            addCampus.pending,
            singleCampus.pending,
            updateCampus.pending,
            campusList.pending,
            deleteCampus.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          state.error =
            action.payload || messages[action.type.split("/")[0]].error;
          action.meta.arg.setLoader(true);
        }
      )
      .addMatcher(
        (action) =>
          [
            addCampus.fulfilled,
            updateCampus.fulfilled,
            campusList.fulfilled,
            deleteCampus.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";

          if (["listCampus"].includes(action.type.split("/")[0])) {
            // console.log("state.campusData", state.campusData);

            const existingData = state.campusData.data;
            const newData = action.payload.campuses.data;

            const mergedData = [...existingData, ...newData].reduce(
              (acc, current) => {
                if (!acc.find((item) => item.id === current.id)) {
                  acc.push(current);
                }
                return acc;
              },
              []
            );

            state.campusData = {
              ...action.payload.campuses,
              data: mergedData,
            };

            // state.campusData = {
            //   ...action.payload.campuses,
            //   data: [...state.campusData.data, ...action.payload.campuses.data],
            // };
            // console.log('state.campusData eeeee',state.campusData);
          }

          // Create
          if ("createCampus" === action.type.split("/")[0]) {
            state.campusData = {
              data: [],
            };
          }

          // Update state in case of edit
          if ("editCampus" === action.type.split("/")[0]) {
            const existingPost = state.campusData.data.find(
              (camp) => camp.id === action.payload.id
            );
            if (existingPost) {
              existingPost.name = action.payload.data.name;
              existingPost.address = action.payload.data.address;
            }
          }

          // Delete
          if ("deleteCampus" === action.type.split("/")[0]) {
            state.campusData = {
              data: [],
            };
          }

          action.meta.arg.setLoader(false);
          // Show toast only in case of add, edit, delete campus
          if (
            ["createCampus", "editCampus", "deleteCampus"].includes(
              action.type.split("/")[0]
            )
          ) {
            campusSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  messages[action.type.split("/")[0]].success,
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            addCampus.rejected,
            updateCampus.rejected,
            singleCampus.rejected,
            campusList.rejected,
            deleteCampus.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          state.error =
            action.payload || messages[action.type.split("/")[0]].error;
          action.meta.arg.setLoader(false);
          campusSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

export default campusSlice.reducer;
